import { useMemo } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  flexRender,
  type ColumnDef,
} from '@tanstack/react-table'
import type { DisplayField, DocumentPreview } from '@/types'
import { pickValue } from '@/lib/normalize'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Timestamp } from 'firebase/firestore'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'

interface DocumentPreviewTableProps {
  documents: DocumentPreview[]
  displayFields: DisplayField[]
  selectedIds: Set<string>
  onSelectionChange: (ids: Set<string>) => void
}

function formatValue(value: unknown, t: TFunction, locale: string): string {
  if (value === null || value === undefined) {
    return '-'
  }
  if (value instanceof Timestamp) {
    return value.toDate().toLocaleString(locale)
  }
  if (typeof value === 'boolean') {
    return value ? t('common.yes') : t('common.no')
  }
  if (typeof value === 'object') {
    return JSON.stringify(value)
  }
  return String(value)
}

export function DocumentPreviewTable({
  documents,
  displayFields,
  selectedIds,
  onSelectionChange,
}: DocumentPreviewTableProps) {
  const { t, i18n } = useTranslation()
  const locale = i18n.resolvedLanguage || 'en'

  const columns = useMemo<ColumnDef<DocumentPreview>[]>(() => {
    const selectColumn: ColumnDef<DocumentPreview> = {
      id: 'select',
      // Selects the rows on the visible page only, so a bulk delete never includes
      // documents the admin has not seen.
      header: ({ table }) => (
        <input
          type="checkbox"
          aria-label={t('documents.preview.selectPage')}
          checked={table.getIsAllPageRowsSelected()}
          ref={(el) => {
            if (el) el.indeterminate = table.getIsSomePageRowsSelected()
          }}
          onChange={table.getToggleAllPageRowsSelectedHandler()}
          className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={row.getIsSelected()}
          onChange={row.getToggleSelectedHandler()}
          className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
        />
      ),
      size: 40,
    }

    const fieldColumns: ColumnDef<DocumentPreview>[] = displayFields.map((field) => ({
      id: field.label,
      // Headers are Firestore field names (technical identifiers), so they are not translated.
      accessorFn: (row) => pickValue(row, field.keys),
      header: field.label,
      cell: ({ row }) => (
        <span className="text-sm text-gray-900">
          {formatValue(pickValue(row.original, field.keys), t, locale)}
        </span>
      ),
    }))

    return [selectColumn, ...fieldColumns]
  }, [displayFields, t, locale])

  const table = useReactTable({
    data: documents,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 20,
      },
    },
    enableRowSelection: true,
    // Key selection by document id so it survives paging and re-fetches.
    getRowId: (row) => row.id,
    onRowSelectionChange: (updater) => {
      const current = Object.fromEntries(Array.from(selectedIds).map((id) => [id, true]))
      const next = typeof updater === 'function' ? updater(current) : updater
      onSelectionChange(new Set(Object.keys(next).filter((id) => next[id])))
    },
    state: {
      rowSelection: Object.fromEntries(Array.from(selectedIds).map((id) => [id, true])),
    },
  })

  const selectedCount = selectedIds.size

  return (
    <Card>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              {t('documents.preview.title')}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {t('documents.preview.summary', { found: documents.length, selected: selectedCount })}
            </p>
          </div>
        </div>

        {documents.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">{t('documents.noneFound')}</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6"
                          style={{ width: header.getSize() }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50">
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="px-4 py-4 whitespace-nowrap sm:px-6">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between border-t border-gray-200 pt-4">
              <div className="text-sm text-gray-700">
                {t('common.pageOf', {
                  page: table.getState().pagination.pageIndex + 1,
                  total: table.getPageCount(),
                })}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                >
                  <ChevronLeft className="w-4 h-4" />
                  {t('common.previous')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                >
                  {t('common.next')}
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </Card>
  )
}
